import React, { ReactElement } from 'react';
import { styled } from 'styled-components';
import { theme } from '../theme';
import { typography } from '../typography';

export type ButtonVariant = 'primary' | 'secondary';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  onClick?: () => void;
  iconLeft?: ReactElement;
  iconRight?: ReactElement;
}

export const ThemeButton: React.FC<ButtonProps> = (props: ButtonProps) => {
  const { variant, iconLeft, iconRight, disabled, ...rest } = props;

  return (
    <ButtonStyle {...rest} $variant={variant ?? 'primary'} disabled={disabled}>
      <>
        {iconLeft && <IconContainer>{iconLeft}</IconContainer>}
        {props.children}
        {iconRight && <IconContainer>{iconRight}</IconContainer>}
      </>
    </ButtonStyle>
  );
};

const getColor = (variant: ButtonVariant, disabled: boolean) => {
  if (variant === 'primary') {
    return disabled ? theme.colors.text.hint : theme.colors.basic['000'];
  }

  return theme.colors.basic['900'];
};

const getBackgroundColor = (variant: ButtonVariant, disabled: boolean) => {
  if (variant === 'primary') {
    return disabled ? theme.colors.button.filled.background.disabled : theme.colors.button.filled.background.enabled;
  }

  if (variant === 'secondary') {
    return disabled ? theme.colors.button.filled.background.disabled : theme.colors.basic['000'];
  }
};

const ButtonStyle = styled.button<{ $variant: ButtonVariant; $disabled?: boolean }>`
  ${typography('body2')};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.s};
  position: relative;
  justify-content: center;

  padding: ${theme.spacing.d} ${theme.spacing.xl};
  color: ${props => getColor(props.$variant, !!props.$disabled)};
  background-color: ${props => getBackgroundColor(props.$variant, !!props.$disabled)};
  border: 1px solid ${props => (props.$variant === 'primary' ? theme.colors.primary['300'] : theme.colors.card.backgroundStroke)};
  border-radius: 990px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const IconContainer = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
`;
